import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

import { IdentityChip, type IdentityChipProps } from './IdentityChip';
import type { Identity } from './types';

/** Defines the chip-group rendering options for multiple identities. */
export interface IdentityChipGroupProps {
  /** Supplies the ordered identities to render as chips. */
  identities: Identity[];
  /** Caps the number of identities rendered before showing overflow. */
  maxVisible?: number;
  /** Applies shared chip props to each rendered identity chip. */
  chipProps?: Omit<IdentityChipProps, 'identity'>;
}

/** Renders a capped set of identity chips with a +N overflow indicator. */
export const IdentityChipGroup: React.FC<IdentityChipGroupProps> = ({
  identities,
  maxVisible = 3,
  chipProps,
}) => {
  const visible = identities.slice(0, maxVisible);
  const hiddenCount = Math.max(0, identities.length - visible.length);

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {visible.map((identity) => (
        <IdentityChip key={identity.username} identity={identity} {...chipProps} />
      ))}
      {hiddenCount > 0 ? (
        <Chip label={`+${hiddenCount}`} size="small" variant={chipProps?.variant ?? 'outlined'} />
      ) : null}
    </Box>
  );
};
